import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Stack,
  Typography, Box, Paper, Checkbox
} from "@mui/material";
import CenteredCard from "../../components/Card/CenteredCardLayout";
import DefaultActions from "../../components/Card/DefaultActions";
import { useAuth, useLoading, useSnackbar } from "../../Context/Contexts";
import { useTranslation } from "react-i18next";
import { CardVoucher, VoucherTransaction, ListQuery } from "../../Types/ExportTypes";
import ResponsivePagination from "../../components/ResponsivePagination";

const CardVoucherPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { code } = params || {};
  const [voucher, setVoucher] = useState<CardVoucher | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [transactions, setTransactions] = useState<VoucherTransaction[]>([]);
  const [showCustomerInfo, setShowCustomerInfo] = useState(false);
  const [showTransactions, setShowTransactions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { showLoading, hideLoading } = useLoading();
  const { t } = useTranslation();

  const [query, setQuery] = useState<ListQuery>({
    page: 0,
    pageSize: 10,
    filter: "",
    filters: [],
  });


  const handlePageChange = (newPage: number) => {
    setQuery({ ...query, page: newPage });
  };


  const fetchVoucher = async (voucherCode: number) => {
    setIsLoading(true);
    showLoading();
    try {
      const result = await authService.get<CardVoucher>(`/cardVoucherApi/Get?code=${voucherCode}`);
      setVoucher(result);
      setTransactions([]);
    } catch (error: any) {
      showSnackbar(`${t("common.failedToFetchOrders")}: ${error.response?.data ?? error.message}`, "error");
      setVoucher(null);
    } finally {
      setIsLoading(false);
      hideLoading();
    }
  };

  const fetchTransactions = async (voucherCode: number) => {
    setLoadingTransactions(true);
    try {
      const result = await authService.post<{ items: VoucherTransaction[]; count: number }>(`/cardVoucherApi/GetTransactions?code=${voucherCode}`, query);
      setTransactions(result.items);
      setTotalCount(result.count);
    } catch (error: any) {
      showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    if (code) fetchVoucher(code);
  }, [code]);

  useEffect(() => {
    if (showTransactions && voucher) {
      fetchTransactions(voucher.code);
    }
  }, [showTransactions, voucher, query]);


  const cancelVoucher = async () => {
    if (!voucher) return;
    showLoading();
    try {
      await authService.post("/cardVoucherApi/Cancel", { code: voucher.code });
      await fetchVoucher(voucher.code);
    } catch (error: any) {
      showSnackbar(`${t("common.failedToCancel")}: ${error.response?.data ?? error.message}`, "error");
    } finally {
      hideLoading();
    }
  };

  return (
    <CenteredCard title={t("common.certificate")} isLoading={isLoading}>
      {voucher && (
        <>
          {!voucher.canceled && (
            <Box sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              maxWidth: "fit-content",
              mb: 2
            }}>
              <DefaultActions onRedeem={cancelVoucher} />
            </Box>
          )}
          <Grid container spacing={2}>
            {voucher.externalCode && (
              <Grid item xs={12} >
                <TextField
                  label={t("common.externalCode")}
                  value={voucher.externalCode}
                  InputLabelProps={{ shrink: true }}
                  disabled={true}
                  fullWidth
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("common.customerNo")}
                value={voucher.customerNo}
                InputLabelProps={{ shrink: true }}
                disabled={true}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("common.name")}
                value={voucher.customerName}
                InputLabelProps={{ shrink: true }}
                disabled={true}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={showCustomerInfo} onChange={(e) => setShowCustomerInfo(e.target.checked)} />}
                label={t("common.showCustomerInfo")}
              />
            </Grid>
            {showCustomerInfo && (

              <Box
                sx={{
                  backgroundColor: "secondary.main",
                  padding: 2,
                  borderRadius: 2,
                  width: "100%"
                }}
              >
                <Grid container spacing={2}>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t("common.email")}
                      value={voucher.customerEmail}
                      InputLabelProps={{ shrink: true }}
                      disabled={true}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t("common.phoneNo")}
                      value={voucher.customerPhone}
                      InputLabelProps={{ shrink: true }}
                      disabled={true}
                      fullWidth
                    />
                  </Grid>

                </Grid>
              </Box>

            )}
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("common.amount")}
                value={`${voucher.amount} ${voucher.currencyCode}`}
                InputLabelProps={{ shrink: true }}
                disabled={true}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t("common.remainingAmount")}
                value={`${voucher.remainingAmount} ${voucher.currencyCode}`}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={true}
              />
            </Grid>

            <Grid item xs={6} >
              <TextField
                label={t("common.appliesToDocumentNo")}
                value={voucher.appliesToDocumentNo}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={true}
              />
            </Grid>

            <Grid item xs={6} >
              <TextField
                label={t("common.created")}
                value={voucher.created ? new Date(voucher.created).toLocaleString() : ""}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={true}
              />
            </Grid>
            <Grid item xs={6} >
              <TextField
                label={t("common.activated")}
                value={voucher.activated ? new Date(voucher.activated).toLocaleString() : ""}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={true}
              />
            </Grid>
            <Grid item xs={6} >
              <TextField
                label={t("common.validUntil")}
                value={voucher.validUntil ? new Date(voucher.validUntil).toLocaleString() : ""}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={true}
              />
            </Grid>

            <Grid item xs={6}>


              <FormControlLabel
                control={<Checkbox checked={voucher.canceled !== null && voucher.canceled !== undefined && voucher.canceled.length > 0} disabled={true} />}
                label={t("common.redeemed")}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={showTransactions} onChange={(e) => setShowTransactions(e.target.checked)} />}
                label={t("common.showTransactions")}
              />
            </Grid>
            {showTransactions && (

              <Grid item xs={12}>
                {loadingTransactions ? (
                  <Typography>{t("common.loading")}...</Typography>
                ) : (<>

                  {transactions.map((tx) => (
                    <Paper
                      key={tx.id}
                      sx={{
                        p: 2,
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        cursor: 'pointer',
                        transition: 'background-color 0.3s',
                        '&:hover': { backgroundColor: '#f0f0f0' }
                      }}
                    >
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {tx.postingDate?.split('T')[0]}
                      </Typography>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" flexWrap="wrap">
                          <Typography variant="body2">
                            {t("common.documentNo")}: {tx.documentNo}
                          </Typography>
                          <Typography variant="body2">
                            {t("common.postingDate")}: {tx.postingDate ? new Date(tx.postingDate).toLocaleString() : ""}
                          </Typography>
                          <Typography variant="body2">
                            {t("common.amount")}: {tx.amount}
                          </Typography>
                          {/*<Typography variant="body2">*/}
                          {/*    {t("common.remainingAmount")}: {tx.remainingAmount}*/}
                          {/*</Typography>*/}
                        </Stack>
                      </Stack>
                    </Paper>


                  ))}
                  {!isLoading && transactions.length === 0 && (
                    <Grid item xs={12}>
                      <Typography align="center">{t("common.noEntriesFound")}</Typography>
                    </Grid>
                  )}

                  <ResponsivePagination
                    sx={{ marginTop: "1rem" }}

                    page={query.page}


                    pageSize={query.pageSize}


                    totalCount={totalCount}


                    onPageChange={handlePageChange}


                  />
                </>
                )}
              </Grid>
            )}

          </Grid>
        </>
      )}
    </CenteredCard>
  );
};

export default CardVoucherPage;